import { getElementLineHeight, getPagesCalculatedData } from '.';

type Blocks = ReturnType<typeof getPagesCalculatedData>;

type Pages = ({ blocks: [Blocks[0]] | [Blocks[0], Blocks[0]] } & (
  | {
      imageMaxHeight?: never;
      height?: never;
      offset?: never;
      scaleDown?: never;
      split?: never;
    }
  | {
      imageMaxHeight?: never;
      height?: never;
      offset?: never;
      scaleDown: boolean;
      split?: never;
    }
  | {
      imageMaxHeight?: never;
      height: number;
      offset: number;
      scaleDown?: never;
      split: boolean;
    }
  | {
      imageMaxHeight: number;
      height?: never;
      offset?: never;
      scaleDown?: never;
      split?: never;
    }
))[][];

interface Params {
  blocks: Blocks;
  pageHeight: number;
  pageWidth: number;
}

const getPagesBlockData = ({ blocks, pageHeight, pageWidth }: Params) => {
  const pages: Pages = [];

  // keep track how much space is available on page
  let pageHeightAvailable = pageHeight;

  // default line height
  let lineHeight = 24;
  // get line height from first text block if there is one
  const textBlocks = document.getElementsByClassName('reader-text');
  if (textBlocks.length > 0) {
    lineHeight = getElementLineHeight(textBlocks[0] as HTMLElement);
  }

  // inspect each block
  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];

    // 1. check if block should be shown side by side with previous block
    const previousBlock = blocks[i - 1];
    const lastPage = pages[pages.length - 1];
    const previousBlockIsAlone =
      lastPage && lastPage[lastPage.length - 1].blocks.length === 1;
    if (previousBlock && previousBlockIsAlone) {
      // check types because we only want to show some combinations side by side
      const typesMatch =
        (block.type === 'image' &&
          ['list', 'text'].indexOf(previousBlock.type) !== -1) ||
        (block.type === 'list' && previousBlock.type === 'image') ||
        (block.type === 'text' && previousBlock.type === 'image');
      if (typesMatch) {
        // estimate block heights with 50% width
        // text and list: 2x
        // image without caption: 0.5x
        // image with caption: 1x
        let heightEstimate = block.height * 2;
        if (block.type === 'image') heightEstimate *= 0.25;
        if (block.data.caption) heightEstimate *= 2;

        let previousHeightEstimate = previousBlock.height * 2;
        if (previousBlock.type === 'image') previousHeightEstimate *= 0.25;
        if (previousBlock.data.caption) previousHeightEstimate *= 2;

        // check if blocks fit on page
        const sideBySideHeight = Math.max(
          heightEstimate,
          previousHeightEstimate
        );
        if (sideBySideHeight < pageHeight) {
          // check if blocks should be added to the current page, this is
          // only done if they fit and there are no other blocks or if there
          // is a single heading block
          // 20 = side by side container top margin
          if (
            (sideBySideHeight <= pageHeight && lastPage.length === 1) ||
            (lastPage.length === 2 &&
              lastPage[0].blocks[0].type === 'heading' &&
              sideBySideHeight + 20 + lastPage[0].blocks[0].height <=
                pageHeight)
          ) {
            // add block next to previous block
            lastPage[lastPage.length - 1].blocks.push(block);
          } else {
            // move previous block to new page and then add current block
            pages.push([lastPage.pop()]);
            pages[pages.length - 1][0].blocks.push(block);
          }

          // check if blocks are images and compare aspect ratios
          if (block.type === 'image' && previousBlock.type === 'image') {
            const aspectRatioDiff =
              block.imageAspectRatio / previousBlock.imageAspectRatio;
            // if the aspect ratios of both images are very close to
            // each other, we want the images to have the same height
            if (aspectRatioDiff < 1.2 && aspectRatioDiff > 0.8) {
              // side by side images have 48% width
              const imageMaxWidth = 0.48 * pageWidth;
              // get max height for images so they have the same height
              const imageMaxHeight = Math.min(
                imageMaxWidth * block.imageAspectRatio,
                imageMaxWidth * previousBlock.imageAspectRatio
              );
              const newLastPage = pages[pages.length - 1];
              newLastPage[newLastPage.length - 1].imageMaxHeight =
                imageMaxHeight;
            }
          }
          // block has been added, handle next block
          pageHeightAvailable = 0;
          continue;
        }
      }
    }

    // 2. check if text block should be split to multiple pages
    if (block.type === 'text' && block.height > pageHeight) {
      // we want to show roughly the same number of lines on each page
      const linesInBlock = block.height / lineHeight;
      const maxLinesPerPage = Math.floor(pageHeight / lineHeight);
      const numberOfPages = Math.ceil(linesInBlock / maxLinesPerPage);
      const linesPerPage = Math.ceil(linesInBlock / numberOfPages);
      let usedLines = 0;
      let height = 0;
      while (usedLines < linesInBlock) {
        const pageLines =
          linesPerPage > linesInBlock - usedLines
            ? linesInBlock - usedLines
            : linesPerPage;
        const offset = usedLines * lineHeight;
        usedLines += pageLines;
        height += pageLines * lineHeight;
        pages.push([
          { blocks: [block], split: true, height: height, offset: offset }
        ]);
      }
      pageHeightAvailable = 0;
      continue;
    }

    // 3. check if block should be added to a new page
    if (
      !pages.length ||
      block.type === 'heading' ||
      block.type === 'pagebreak' ||
      block.height + block.marginTop > pageHeightAvailable
    ) {
      pages.push([{ blocks: [block] }]);
      pageHeightAvailable = pageHeight - block.height;

      // check if block is only slightly higher than page and could be scaled
      if (block.height > pageHeight && block.height * 0.9 <= pageHeight) {
        pages[pages.length - 1][0].scaleDown = true;
      }
      continue;
    }

    // 4. default: add block to current page
    pages[pages.length - 1].push({ blocks: [block] });
    pageHeightAvailable -= block.height + block.marginTop;
  }

  return pages;
};

export default getPagesBlockData;
